import React from "react";
import { FileText, Inbox, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";
import { fmtDate, fmtSize, shortType } from "@/lib/formatters";

const DocumentRow = ({ doc, leadName, onDownload, downloadLabel }) => (
  <tr className="border-b border-[color:var(--border-subtle)] last:border-0 transition-colors hover:bg-[var(--surface-2)]">
    <td className="px-4 py-3">
      <div className="flex items-center gap-2 font-medium text-[var(--brand-charcoal)]">
        <FileText className="h-4 w-4 shrink-0 text-[var(--brand-burgundy)]" />
        <span className="max-w-[200px] truncate">{doc.original_filename}</span>
      </div>
    </td>
    <td className="px-4 py-3"><span className="rounded bg-[var(--surface-2)] px-2 py-0.5 text-xs font-semibold text-[var(--muted-ink)]">{shortType(doc.mime)}</span></td>
    <td className="hidden px-4 py-3 text-[var(--muted-ink)] sm:table-cell">{fmtSize(doc.size)}</td>
    <td className="hidden px-4 py-3 text-[var(--muted-ink)] md:table-cell">{leadName(doc.lead_id)}</td>
    <td className="hidden px-4 py-3 text-xs text-[var(--muted-ink)] lg:table-cell">{fmtDate(doc.created_at)}</td>
    <td className="px-4 py-3 text-right">
      <Button size="sm" onClick={() => onDownload(doc)} className="h-8 rounded-lg bg-[var(--brand-burgundy)] font-semibold text-white hover:bg-[var(--brand-burgundy-dark)]" data-testid="admin-documents-download-button">
        <Download className="mr-1.5 h-3.5 w-3.5" />{downloadLabel}
      </Button>
    </td>
  </tr>
);

export const DocumentsTable = ({ docs, leadName, onDownload }) => {
  const { t } = useLanguage();
  return (
    <div className="overflow-hidden rounded-2xl border border-[color:var(--border-subtle)] bg-white shadow-[var(--shadow-sm)]">
      {docs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="admin-docs-empty">
          <Inbox className="h-10 w-10 text-[var(--muted-ink)]" />
          <p className="mt-3 text-sm text-[var(--muted-ink)]">{t.admin.noDocs}</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm" data-testid="admin-docs-table">
            <thead>
              <tr className="border-b border-[color:var(--border-subtle)] bg-[var(--surface-2)] text-xs uppercase tracking-wide text-[var(--muted-ink)]">
                <th className="px-4 py-3 font-semibold">{t.admin.colFile}</th>
                <th className="px-4 py-3 font-semibold">{t.admin.colType}</th>
                <th className="hidden px-4 py-3 font-semibold sm:table-cell">{t.admin.colSize}</th>
                <th className="hidden px-4 py-3 font-semibold md:table-cell">{t.admin.colLead}</th>
                <th className="hidden px-4 py-3 font-semibold lg:table-cell">{t.admin.colDate}</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <DocumentRow key={d.id} doc={d} leadName={leadName} onDownload={onDownload} downloadLabel={t.admin.download} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
